import { useEffect, useState, type ReactNode } from "react";
import type { AccountOptionsDto, AccountRef, AccountUse, GroupOption, References } from "@delta/shared";
import { useLang, type T } from "../../i18n";
import { api } from "../../lib/api";
import { referencesText } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { indentText } from "../../lib/indent";

// Pieces shared by the accounting configuration screens and the screens that choose an account.

/** Account, type and tax type lists for dropdowns. null while loading; `failed` when the user may not read them. */
export function useAccountOptions(): { opts: AccountOptionsDto | null; failed: boolean; reload(): void } {
  const [opts, setOpts] = useState<AccountOptionsDto | null>(null);
  const [failed, setFailed] = useState(false);
  const [n, setN] = useState(0);
  useEffect(() => {
    api<AccountOptionsDto>("GET", "/accounting/options").then((o) => { setOpts(o); setFailed(false); }, () => setFailed(true));
  }, [n]);
  // Read again when the window comes back into focus, so an account just created in another tab is offered without
  // closing the form.
  useEffect(() => {
    const again = () => { if (document.visibilityState === "visible") setN((x) => x + 1); };
    window.addEventListener("focus", again);
    document.addEventListener("visibilitychange", again);
    return () => { window.removeEventListener("focus", again); document.removeEventListener("visibilitychange", again); };
  }, []);
  return { opts, failed, reload: () => setN((x) => x + 1) };
}

/** "900101 Example: cash" with the code in Western digits, as the prototype's accName shows it. */
export function AccName({ a }: { a: AccountRef | null | undefined }) {
  const { L } = useLang();
  if (!a) return <span className="muted">–</span>;
  return <span className="acc-cell trunc" title={`${a.code} ${L(a)}`}><span className="num muted">{a.code}</span> {L(a)}</span>;
}
export const accText = (L: (o: { nameEn: string; nameAr: string }) => string, a: AccountRef | null | undefined) => (a ? `${a.code} ${L(a)}` : "");

/** A group as selectors show it: its whole path from the class ("Assets › Current assets › Cash"), its code first. */
export const groupPath = (L: (o: { nameEn: string; nameAr: string }) => string, g: GroupOption) => `${g.code ? g.code + " " : ""}${L({ nameEn: g.pathEn, nameAr: g.pathAr })}`;
/** Groups for a select, in tree order, indented by level, with their code. */
export const groupPairs = (L: (o: { nameEn: string; nameAr: string }) => string, types: GroupOption[], level1Id?: number): [string, string][] =>
  types.filter((g) => !level1Id || g.level1Id === level1Id).map((g) => [g.id, `${indentText(g.depth - 1, "\u2003")}${g.code ? g.code + " " : ""}${L(g)}`]);

/**
 * Accounts grouped under their group (prototype journal accOpts), each group labelled with its path, in tree order, at
 * any depth. A group with nothing beneath it is listed too, with a disabled line saying it takes no entries, so an
 * entry created as a group does not simply vanish from the list. `q` keeps the accounts whose code, name or path (in
 * either language) contains it.
 */
export function AccountOptions({ opts, q = "" }: { opts: AccountOptionsDto | null; q?: string }) {
  const { L, t } = useLang();
  if (!opts) return null;
  const needle = q.trim().toLowerCase();
  const parents = new Set(opts.types.map((g) => g.parentId).filter(Boolean));
  const hit = (g: GroupOption, a?: { code: string; nameEn: string; nameAr: string }) => !needle ||
    [g.code ?? "", g.pathEn, g.pathAr, a?.code ?? "", a?.nameEn ?? "", a?.nameAr ?? ""].some((x) => x.toLowerCase().includes(needle));
  return <>{opts.types.map((g) => {
    const accs = opts.accounts.filter((a) => a.typeId === g.id && hit(g, a));
    if (accs.length) return <optgroup key={g.id} label={groupPath(L, g)}>{accs.map((a) => <option key={a.id} value={a.id}>{a.code} {L(a)}</option>)}</optgroup>;
    const empty = !parents.has(g.id) && !opts.accounts.some((a) => a.typeId === g.id);
    return empty && hit(g) ? <optgroup key={g.id} label={groupPath(L, g)}><option value={`group:${g.id}`} disabled data-group="true">{t("accGroupNoEntries")}</option></optgroup> : null;
  })}</>;
}
/** Flat [id, "code name"] pairs for a Custom search select. */
export const accountPairs = (L: (o: { nameEn: string; nameAr: string }) => string, opts: AccountOptionsDto | null): [string, string][] =>
  (opts?.accounts ?? []).map((a) => [a.id, `${a.code} ${L(a)}`]);

/**
 * An account select: posting accounts only, under their groups' paths. `search` adds a filter box above it and, once
 * an account is chosen, its whole path beneath, so accounts with similar names are told apart.
 */
export function AccountSelect({ id, value, onChange, opts, failed, empty, search }: { id: string; value: string; onChange(v: string): void; opts: AccountOptionsDto | null; failed?: boolean; empty?: string; search?: boolean }) {
  const { t, L } = useLang();
  const [q, setQ] = useState("");
  const cur = opts?.accounts.find((a) => a.id === value);
  // While a filter is typed, the chosen account stays listed first so the select keeps showing it.
  const pinned = q.trim() && cur && opts ? cur : null;
  const select = (
    <select id={id} className="control" value={value} onChange={(e) => onChange(e.target.value)} disabled={failed} title={failed ? t("accountsUnavailable") : undefined}>
      <option value="">{empty ?? t("choose")}</option>
      {pinned && <option value={pinned.id}>{pinned.code} {L(pinned)}</option>}
      <AccountOptions opts={pinned ? { ...opts!, accounts: opts!.accounts.filter((a) => a.id !== value) } : opts} q={q} />
    </select>
  );
  if (!search) return select;
  const g = cur && opts?.types.find((x) => x.id === cur.typeId);
  return (
    <div className="acc-pick">
      <input type="search" className="control acc-pick-q" data-testid={`${id}-q`} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("accSearchPh")} aria-label={t("accSearchPh")} disabled={failed} />
      {select}
      {g && <div className="acc-pick-path muted" data-testid={`${id}-path`}>{groupPath(L, g)} › {cur!.code} {L(cur!)}</div>}
    </div>
  );
}

/** Where an account is linked, as "1 tax types, 2 item kinds"; empty when it is not linked. */
export const usageText = (t: T, u: AccountUse) => referencesText(t, Object.fromEntries(Object.entries(u).filter(([, n]) => n)) as References);

/** A field or card whose data waits for a later module (journal postings, stock documents). */
export const Waiting = ({ text }: { text: string }) => <div className="dep-note" data-testid="pending-field"><Icon name="lock" /><span>{text}</span></div>;

export function Card({ title, count, children, raw, actions }: { title: string; count?: number; children: ReactNode; raw?: boolean; actions?: ReactNode }) {
  return <section className="card"><div className="card-head"><h2>{title}{count != null && <> <span className="count num">{count}</span></>}</h2>{actions}</div>{raw ? children : <div className="card-body">{children}</div>}</section>;
}
